import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { DialogService, GridContentWidget, PageTabsMainTabGridSize } from '@eforall/dashboard';
import { MentorTrimesterAvailability, VolunteerMentorPool } from '@interfaces';
import { DomainDataService, TrimesterService } from '../../../../../services';
import { VolunteeringAreaService } from '../../../volunteering-area.service';
import { VolunteeringMentorsService } from '../../volunteering-mentors.service';
import { getSetup } from './grid/get-setup';

@Component({
	selector: 'mentor-pool-view-part',
	standalone: true,
	imports: [GridContentWidget],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './mentor-pool.part.html',
})
export class MentorPoolViewPart implements OnInit {

	private readonly util = inject(UtilityService);
	private readonly areaService = inject(VolunteeringAreaService);
	private readonly pageService = inject(VolunteeringMentorsService);
	private readonly domainDataService = inject(DomainDataService);
	private readonly dialogService = inject(DialogService);
	private readonly trimesterService = inject(TrimesterService);

	ngOnInit() {
		this.areaService.getVolunteeringData();
	}

	public setup = computed(() => {
		// change the columns when the selected trimester changes
		const selectedTrimester = this.trimesterService.selectedTrimester();
		return getSetup(this.areaService, this.util, this.domainDataService, this.dialogService, this.areaService.app);
	});

	public pageTabsGridSize = computed<PageTabsMainTabGridSize | undefined>(() => {
		if (this.pageService.layout()) return { layout: this.pageService.layout()!, viewSelector: true };
		return undefined;
	});

	public mentorPool = computed<VolunteerMentorPool[]>(() => {
		const data = this.areaService.volunteeringData();
		if (!data) return [];


		// get mentor sign-up status map for the current and next trimester
		const signUpMentorStatusMap = data.signUpMentors.reduce((map, signUp) => {
			const volunteerId = signUp.volunteerId;

			if (!map[volunteerId]) {
				map[volunteerId] = {
					selectedTrimesterStatus: this.defaultMentorAvailability,
					selectedNextTrimesterStatus: this.defaultMentorAvailability
				}
			}

			if (signUp.trimesterId === data.selectedTrimester.trimesterId) {
				map[volunteerId].selectedTrimesterStatus = {
					status: signUp.volunteerSignupMentorStatus,
					selectedLanguage: signUp.selectedLanguage,
					assignedLanguageId: signUp.assignedLanguageId
				};
			} else if (signUp.trimesterId === data.selectedNextTrimester?.trimesterId) {
				map[volunteerId].selectedNextTrimesterStatus = {
					status: signUp.volunteerSignupMentorStatus,
					selectedLanguage: signUp.selectedLanguage,
					assignedLanguageId: signUp.assignedLanguageId
				};
			}

			return map;
		}, {} as Record<number, { selectedTrimesterStatus: MentorTrimesterAvailability, selectedNextTrimesterStatus: MentorTrimesterAvailability }>);

		// map mentor approved volunteers to their sign-up statuses for the current and next trimester
		return data.volunteers.filter(v => v.isMentorApproved).map(volunteer => {
			const statusMap = signUpMentorStatusMap[volunteer.volunteerId];

			return {
				...volunteer,
				volunteerSignupMentorStatus: {
					selectedTrimester: statusMap ? statusMap.selectedTrimesterStatus : { ...this.defaultMentorAvailability },
					selectedNextTrimester: statusMap ? statusMap.selectedNextTrimesterStatus : { ...this.defaultMentorAvailability }
				}

			};
		});
	});

	private defaultMentorAvailability: MentorTrimesterAvailability = {
		status: undefined,
		selectedLanguage: undefined,
		assignedLanguageId: undefined
	};

}